import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { List } from '@wds/list';
import { safeRest } from '@wds/utils';
import { DATA_VIZ_THEMES, SEMANTIC_COLORS } from '@wds/helpers';

const MenuItem = forwardRef(
  (
    {
      addOn,
      addOnPlacement,
      color,
      onClick,
      children,
      component,
      disabled,
      isNavigable,
      marked,
      upgrade,
      groupindex,
      handleMenuClose,
      index,
      role,
      ...rest
    },
    ref
  ) => {
    const onHandleClick = (e) => {
      if (onClick) onClick(e);
      if (handleMenuClose) handleMenuClose(e);
      e.stopPropagation();
    };

    return (
      <List.Item
        addOnLeft={addOnPlacement === 'left' ? addOn : undefined}
        addOnRight={addOnPlacement === 'right' ? addOn : undefined}
        color={color}
        component={component}
        data-group={groupindex}
        disabled={disabled}
        index={index}
        isNavigable={isNavigable}
        marked={marked}
        onClick={onHandleClick}
        role={role}
        upgrade={upgrade}
        tabIndex={disabled ? -1 : 0}
        ref={ref}
        {...safeRest(rest)}
      >
        {children}
      </List.Item>
    );
  }
);

MenuItem.propTypes = {
  /**
   * Add-on for menu items just as you would use them for ListItems
   */
  addOn: PropTypes.node,
  /**
   * Specify the add-on placement within Item
   */
  addOnPlacement: PropTypes.oneOf(['left', 'right']),
  /**
   * Menu Item content
   */
  children: PropTypes.node.isRequired,
  /**
   * Color variants
   */
  color: PropTypes.oneOfType([
    PropTypes.oneOf(SEMANTIC_COLORS),
    PropTypes.oneOf(DATA_VIZ_THEMES),
  ]),
  /**
   * In combination with `isNavigable` you can pass a custom component that will be
   * rendered in place of a regular a element. By default it is `a` if isNaviagable is true
   */
  component: PropTypes.func,
  /**
   * If true, item will be disabled
   */
  disabled: PropTypes.bool,
  /**
   * Menu group index
   */
  groupindex: PropTypes.number,
  /**
   * Data index of current ListItem
   */
  index: PropTypes.number,
  /**
   * If true, add a mark to the left-hand side of a item.
   * Take notice that the label does not shift to accommodate for the mark;
   * it stays in its original location.
   */
  marked: PropTypes.bool,
  /**
   * onClick handler for ListItem
   * function(event: object) => void
   */
  onClick: PropTypes.func,
  /**
   * Add special "upgrade" highlight styles to item.
   */
  upgrade: PropTypes.bool,
  /**
   * If true, item can be a navigational component
   */
  isNavigable: PropTypes.bool,
  /**
   * The href location of your nav target
   */
  navigationTarget: PropTypes.string,
  /**
   * If true, ListItem will be focused on mount
   */
  isFocused: PropTypes.bool,
  /**
   * on Menu close handler
   * function(event: object) => void
   */
  handleMenuClose: PropTypes.func,
  /**
   * Aria role for Menu Item
   */
  role: PropTypes.string,
};

MenuItem.defaultProps = {
  addOn: undefined,
  addOnPlacement: 'left',
  color: undefined,
  component: undefined,
  disabled: false,
  groupindex: undefined,
  index: undefined,
  marked: false,
  onClick: undefined,
  upgrade: false,
  isNavigable: false,
  navigationTarget: undefined,
  isFocused: false,
  handleMenuClose: undefined,
  role: 'menuitem',
};

MenuItem.displayName = 'MenuItem';

export default MenuItem;
